import { useAuth0 } from "@auth0/auth0-react";
import { Button, notification, Select } from "antd";
import { useState } from "react";
import { ReactSVG } from "react-svg";
import { sendRefundRequest } from "../../utils/billing";
import { formatDate } from "../../utils/luxonDateTimeHelper";
import "./refund.scss";

const Refund = ({ invoice: initialInvoice }) => {
  const { getIdTokenClaims } = useAuth0();

  // Use state for invoice to allow updating it
  const [invoice, setInvoice] = useState(initialInvoice);
  const [feedbackOpened, setFeedbackOpened] = useState(false);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Refund reason options
  const refundReasons = [
    "Duplicate Payment",
    "Wrong Amount Charged",
    "Product/Service Not Received",
    "Billing Error",
    "Unsatisfied with Service",
    "Subscription Canceled",
    "Unauthorized Transaction",
    "Defective/Damaged Product",
    "Delayed Service Delivery",
    "Changed Mind",
  ];

  const onSubmit = async () => {
    if (!reason) {
      setErrorMessage(
        "<p class='text-red-500 mb-2'>Please select a reason for the refund request.</p>"
      );
      return;
    }

    try {
      const token = await getIdTokenClaims();
      if (!token) return;

      const rawToken = token.__raw;

      await sendRefundRequest(rawToken, {
        id: Number(invoice.id),
        reason,
        message: comment,
      });

      // Update invoice status locally to instantly reflect the change
      setInvoice({
        ...invoice,
        refund_status: "2", // Update to refund requested status
      });

      // Show success notification
      notification.success({
        message: "Refund Request Submitted",
        description: "Your refund request has been submitted successfully.",
        placement: "bottomRight",
      });

      // Close the modal after submission
      setFeedbackOpened(false);
      setReason(null);
      setComment("");
      setErrorMessage("");
    } catch (error) {
      // Show error notification
      notification.error({
        message: "Submission Failed",
        description:
          error.message || "Failed to submit refund request. Please try again.",
        placement: "bottomRight",
      });
      console.error("Error submitting refund request:", error);
    }
  };

  // Format currency with appropriate symbol
  const formatCurrency = (amount, currency) => {
    if (amount === "0") return "Free";
    const currencySymbol = currency === "USD" ? "$" : currency;
    return `${currencySymbol}${(amount / 100).toFixed(2)}`;
  };

  return (
    <div>
      <Button
        className="rounded-xl"
        onClick={() => setFeedbackOpened(!feedbackOpened)}
        disabled={invoice.refund_status !== "0" || invoice.amount_paid == "0"}
      >
        <ReactSVG src={"/assets/icons/refund.svg"} />
        {invoice.refund_status == "2"
          ? "Refund Requested"
          : invoice.refund_status == "1"
          ? "Refund Processed"
          : invoice.refund_status == "3"
          ? "Refund Declined"
          : "Refund"}
      </Button>
      <div
        className={`w-[30rem] shadow-lg fixed transition right-4 z-40 bg-white rounded-2xl border border-gray-300 ${
          feedbackOpened ? "feedback-animate-in" : "feedback-animate-out"
        }`}
      >
        <div className="flex p-4 text-black items-center border-b border-gray-300 justify-between">
          <p className={"text-xl font-medium"}>Refund Request</p>
          <ReactSVG
            onClick={() => setFeedbackOpened(false)}
            src={"/assets/icons/cancel.svg"}
            className={"w-6 text-gray-500 cursor-pointer"}
          />
        </div>

        {/* Invoice Details */}
        <div className="p-4 border-b text-left">
          <h3 className="text-base font-medium mb-2">Invoice Details</h3>
          <div className="grid grid-cols-2 gap-y-2 text-sm">
            <div className="text-gray-500">Invoice ID:</div>
            <div className="font-medium">
              {invoice.reference_id || invoice.id}
            </div>

            <div className="text-gray-500">Payment Date:</div>
            <div className="font-medium">
              {formatDate(invoice.payment_time)}
            </div>

            <div className="text-gray-500">Amount:</div>
            <div className="font-medium">
              {formatCurrency(invoice.amount_paid, invoice.currency)}
            </div>

            <div className="text-gray-500">Tax:</div>
            <div className="font-medium">
              {formatCurrency(invoice.tax_amount, invoice.currency)}
            </div>

            <div className="text-gray-500">Service:</div>
            <div className="font-medium">{invoice.service_name}</div>

            <div className="text-gray-500">Organization:</div>
            <div className="font-medium">{invoice.org_name}</div>
          </div>
        </div>

        {/* Reason for Refund Dropdown */}
        <div className="px-4 py-2 border-b border-gray-300 text-left">
          <h3 className="text-sm font-medium mb-1">Reason for Refund</h3>
          <Select
            placeholder="Select a reason"
            className="w-full mb-3 text-left"
            value={reason}
            onChange={(value) => setReason(value)}
            options={refundReasons.map((reason) => ({
              value: reason,
              label: reason,
            }))}
          />

          <h3 className="text-sm font-medium mb-1">Additional Comments</h3>
          <textarea
            className="w-full outline-none p-2 h-20 border border-gray-300 rounded-xl bg-gray-100 text-sm resize-none text-left"
            placeholder="Please provide any additional details about your refund request..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        {/* Submit Button */}
        <div className={"px-4 py-2"}>
          <div dangerouslySetInnerHTML={{ __html: errorMessage }}></div>

          <Button
            type="primary"
            className="rounded-xl mb-3 w-full"
            onClick={onSubmit}
          >
            Submit Request
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Refund;
